// This file is auto-generated by @hey-api/openapi-ts

import { client as _heyApiClient } from './client.gen'
import type { Client, Options as ClientOptions, TDataShape } from '@hey-api/client-axios'
import type {
  CollectLiveTranscriptionAudioData,
  CollectLiveTranscriptionAudioError,
  CollectLiveTranscriptionCloseData,
  CollectLiveTranscriptionCloseError,
  CollectLiveTranscriptionCloseResponse,
  CollectLiveTranscriptionStartData,
  CollectLiveTranscriptionStartError,
  CollectLiveTranscriptionStartResponse,
  CollectLiveTranscriptionStreamData,
  CollectLiveTranscriptionStreamError,
  CollectSessionDetailData,
  CollectSessionDetailResponse,
  CollectSessionFinalizeData,
  CollectSessionFinalizeResponse,
  CollectSessionOpenData,
  CollectSessionOpenResponse,
  CollectSessionPatchAnswersData,
  CollectSessionPatchAnswersResponse,
  CollectSessionProbeQuestionData,
  CollectSessionProbeQuestionResponse,
  CollectSessionSealPartialData,
  CollectSessionSealPartialResponse,
  CollectSignalRuntimeData,
  CollectSignalRuntimeResponse,
  CollectVoiceInputData,
  CollectVoiceInputError,
  CollectVoiceInputResponse,
} from './types.gen'

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean> = ClientOptions<
  TData,
  ThrowOnError
> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>
}

export class CollectService {
  /**
   * Get the questionnaire behind a Signal link
   */
  public static collectSignalRuntime<ThrowOnError extends boolean = true>(
    options: Options<CollectSignalRuntimeData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).get<CollectSignalRuntimeResponse, unknown, ThrowOnError>({
      url: '/signals/{link_token}',
      ...options,
    })
  }

  /**
   * Open or resume a response session
   */
  public static collectSessionOpen<ThrowOnError extends boolean = true>(
    options: Options<CollectSessionOpenData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<CollectSessionOpenResponse, unknown, ThrowOnError>({
      url: '/signals/{link_token}/sessions',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })
  }

  /**
   * Get a response session
   */
  public static collectSessionDetail<ThrowOnError extends boolean = true>(
    options: Options<CollectSessionDetailData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).get<CollectSessionDetailResponse, unknown, ThrowOnError>({
      url: '/sessions/{session_id}',
      ...options,
    })
  }

  /**
   * Upsert answers on an in-progress response session
   */
  public static collectSessionPatchAnswers<ThrowOnError extends boolean = true>(
    options: Options<CollectSessionPatchAnswersData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).patch<CollectSessionPatchAnswersResponse, unknown, ThrowOnError>({
      url: '/sessions/{session_id}/answers',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })
  }

  /**
   * Generate a probing follow-up for an open-text answer
   */
  public static collectSessionProbeQuestion<ThrowOnError extends boolean = true>(
    options: Options<CollectSessionProbeQuestionData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<CollectSessionProbeQuestionResponse, unknown, ThrowOnError>({
      url: '/sessions/{session_id}/questions/{question_id}/probe',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })
  }

  /**
   * Finalize a response session as completed
   */
  public static collectSessionFinalize<ThrowOnError extends boolean = true>(
    options: Options<CollectSessionFinalizeData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<CollectSessionFinalizeResponse, unknown, ThrowOnError>({
      url: '/sessions/{session_id}/finalize',
      ...options,
    })
  }

  /**
   * Seal a response session as partial
   */
  public static collectSessionSealPartial<ThrowOnError extends boolean = true>(
    options: Options<CollectSessionSealPartialData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<CollectSessionSealPartialResponse, unknown, ThrowOnError>({
      url: '/sessions/{session_id}/seal-partial',
      ...options,
    })
  }

  /**
   * Start live transcription on a response session.
   * Marks the response session as live-transcribing. The frontend then opens the SSE ``stream`` endpoint and POSTs audio chunks to the ``audio``endpoint for the same session. The backend holds the single connection to the upstream speech-to-text service on the frontend's behalf.
   */
  public static collectLiveTranscriptionStart<ThrowOnError extends boolean = true>(
    options: Options<CollectLiveTranscriptionStartData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<
      CollectLiveTranscriptionStartResponse,
      CollectLiveTranscriptionStartError,
      ThrowOnError
    >({
      url: '/sessions/{session_id}/live-transcription/start',
      ...options,
    })
  }

  /**
   * SSE stream of live-transcription events.
   * Server-Sent Events stream carrying ``transcript``, ``signal_fill``, ``error`` and ``done`` events for a live-transcription session. Opened with the single-use ``stream_ticket`` from ``start``, which expires within seconds; reconnecting means calling ``start`` again for a fresh one. The handler owns the upstream connection for the session's lifetime. A ``transcript`` event carries ``question_key``: the question that was on screen when that speech was captured. Render the text against that question and not against whatever is showing — a final for the sentence the respondent was still finishing arrives after they have been moved on, so placing it by what is on screen shows their last answer under the next question. Every session transcribes *and* fills in the signal: each final transcript snippet triggers a ``signal_fill`` event carrying the survey answers extracted from everything said so far, each with an ``answered`` flag telling the frontend whether the respondent is presumably done with that question. A ``signal_fill`` event may also carry ``feedback``: a one-shot message to show the respondent when what they said could not be used for a question (an unsupported choice, several picks where one is allowed, a number out of range). A null ``feedback`` means there is nothing new to show, not that a previous message should be cleared. Consumed by ``EventSource``, which cannot set headers, so the credential travels as the ``stream_ticket`` query parameter here — a ticket rather than the session's access token, because URLs end up in access logs.
   */
  public static collectLiveTranscriptionStream<ThrowOnError extends boolean = true>(
    options: Options<CollectLiveTranscriptionStreamData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).get<unknown, CollectLiveTranscriptionStreamError, ThrowOnError>({
      url: '/sessions/{session_id}/live-transcription/stream',
      ...options,
    })
  }

  /**
   * Upload a raw PCM16 audio chunk for a live-transcription session.
   * Body is a raw ``audio/pcm;rate=16000`` mono 16-bit little-endian blob of at most 65536 bytes. The chunk is buffered and forwarded to the upstream speech-to-text service by the SSE handler. Accepted while the buffer is within its cap even if nothing is draining it; once it is full the oldest audio is dropped and the upload fails, and the client should close the session and start a new one.
   *
   * ``X-Signal-Question`` must carry the id of the question that was on screen when this audio was captured, so the answers extracted from it are credited to the question it was spoken to. It is required: audio nobody attributes cannot be read without guessing, and a guess is what credits the tail of one answer to the next question.
   *
   * ``X-Signal-Turn`` carries the turn of a probing question's conversation that was on screen: 0 for the question itself, N for its Nth follow-up from ``/probe``. Omitted means 0.
   */
  public static collectLiveTranscriptionAudio<ThrowOnError extends boolean = true>(
    options: Options<CollectLiveTranscriptionAudioData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<unknown, CollectLiveTranscriptionAudioError, ThrowOnError>({
      url: '/sessions/{session_id}/live-transcription/audio',
      ...options,
      headers: {
        'Content-Type': 'audio/pcm;rate=16000',
        ...options?.headers,
      },
    })
  }

  /**
   * Request graceful close of a live-transcription session.
   * Signals the SSE handler to stop, drain any in-flight transcripts and close the upstream connection. Safe to call multiple times.
   */
  public static collectLiveTranscriptionClose<ThrowOnError extends boolean = true>(
    options: Options<CollectLiveTranscriptionCloseData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<
      CollectLiveTranscriptionCloseResponse,
      CollectLiveTranscriptionCloseError,
      ThrowOnError
    >({
      url: '/sessions/{session_id}/live-transcription/close',
      ...options,
    })
  }

  /**
   * Read the answer to one question out of one recording.
   * Takes a whole recording of the respondent speaking their answer to a single question and returns the answer that question takes, or a sentence to read back to them when what they said cannot be used. This is the non-streaming counterpart to the ``live-transcription`` endpoints: the answer arrives in one response instead of over an SSE stream, and the transcript is not the product — a transcript alone is the wrong thing to put in the field, because "rene at caplena dot com" is not an email address and "fifteen" is not a point on a scale of ten.
   *
   * Body is the recording itself, in any audio format the analysis models read (``audio/webm``, ``audio/mp4``, ``audio/ogg``, ``audio/wav``, ``audio/pcm``, …), of at most 10485760 bytes. The format is taken from the bytes, not from ``Content-Type``.
   *
   * ``X-Signal-Question`` must carry the id of the question the recording answers: it is what the recording is read against, and it decides both what a valid answer looks like and what the feedback talks about.
   *
   * Either ``value`` or ``feedback`` carries the outcome. A recording that answered the question comes back with a ``value`` and no ``feedback``; one that did not comes back with ``feedback`` — a single sentence in the survey's language, addressed to the respondent — and a null ``value``. A recording with nothing usable in it and nothing worth saying about it has neither, while one the models could not read at all is an error (``signals.voice_input.failed``) rather than an empty answer. ``transcript`` is what was said, for showing them what was heard; it is never the answer to store. Nothing is persisted: submit the answer through the session endpoint as usual.
   */
  public static collectVoiceInput<ThrowOnError extends boolean = true>(
    options: Options<CollectVoiceInputData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<CollectVoiceInputResponse, CollectVoiceInputError, ThrowOnError>({
      url: '/sessions/{session_id}/voice-input',
      ...options,
      headers: {
        'Content-Type': 'audio/aac',
        ...options?.headers,
      },
    })
  }
}

export class SignalsService {
  /**
   * Start live transcription on a response session.
   * Marks the response session as live-transcribing. The frontend then opens the SSE ``stream`` endpoint and POSTs audio chunks to the ``audio``endpoint for the same session. The backend holds the single connection to the upstream speech-to-text service on the frontend's behalf.
   */
  public static collectLiveTranscriptionStart<ThrowOnError extends boolean = true>(
    options: Options<CollectLiveTranscriptionStartData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<
      CollectLiveTranscriptionStartResponse,
      CollectLiveTranscriptionStartError,
      ThrowOnError
    >({
      url: '/sessions/{session_id}/live-transcription/start',
      ...options,
    })
  }

  /**
   * SSE stream of live-transcription events.
   * Server-Sent Events stream carrying ``transcript``, ``signal_fill``, ``error`` and ``done`` events for a live-transcription session. Opened with the single-use ``stream_ticket`` from ``start``, which expires within seconds; reconnecting means calling ``start`` again for a fresh one. The handler owns the upstream connection for the session's lifetime. A ``transcript`` event carries ``question_key``: the question that was on screen when that speech was captured. Render the text against that question and not against whatever is showing — a final for the sentence the respondent was still finishing arrives after they have been moved on, so placing it by what is on screen shows their last answer under the next question. Every session transcribes *and* fills in the signal: each final transcript snippet triggers a ``signal_fill`` event carrying the survey answers extracted from everything said so far, each with an ``answered`` flag telling the frontend whether the respondent is presumably done with that question. A ``signal_fill`` event may also carry ``feedback``: a one-shot message to show the respondent when what they said could not be used for a question (an unsupported choice, several picks where one is allowed, a number out of range). A null ``feedback`` means there is nothing new to show, not that a previous message should be cleared. Consumed by ``EventSource``, which cannot set headers, so the credential travels as the ``stream_ticket`` query parameter here — a ticket rather than the session's access token, because URLs end up in access logs.
   */
  public static collectLiveTranscriptionStream<ThrowOnError extends boolean = true>(
    options: Options<CollectLiveTranscriptionStreamData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).get<unknown, CollectLiveTranscriptionStreamError, ThrowOnError>({
      url: '/sessions/{session_id}/live-transcription/stream',
      ...options,
    })
  }

  /**
   * Upload a raw PCM16 audio chunk for a live-transcription session.
   * Body is a raw ``audio/pcm;rate=16000`` mono 16-bit little-endian blob of at most 65536 bytes. The chunk is buffered and forwarded to the upstream speech-to-text service by the SSE handler. Accepted while the buffer is within its cap even if nothing is draining it; once it is full the oldest audio is dropped and the upload fails, and the client should close the session and start a new one.
   *
   * ``X-Signal-Question`` must carry the id of the question that was on screen when this audio was captured, so the answers extracted from it are credited to the question it was spoken to. It is required: audio nobody attributes cannot be read without guessing, and a guess is what credits the tail of one answer to the next question.
   *
   * ``X-Signal-Turn`` carries the turn of a probing question's conversation that was on screen: 0 for the question itself, N for its Nth follow-up from ``/probe``. Omitted means 0.
   */
  public static collectLiveTranscriptionAudio<ThrowOnError extends boolean = true>(
    options: Options<CollectLiveTranscriptionAudioData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<unknown, CollectLiveTranscriptionAudioError, ThrowOnError>({
      url: '/sessions/{session_id}/live-transcription/audio',
      ...options,
      headers: {
        'Content-Type': 'audio/pcm;rate=16000',
        ...options?.headers,
      },
    })
  }

  /**
   * Request graceful close of a live-transcription session.
   * Signals the SSE handler to stop, drain any in-flight transcripts and close the upstream connection. Safe to call multiple times.
   */
  public static collectLiveTranscriptionClose<ThrowOnError extends boolean = true>(
    options: Options<CollectLiveTranscriptionCloseData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<
      CollectLiveTranscriptionCloseResponse,
      CollectLiveTranscriptionCloseError,
      ThrowOnError
    >({
      url: '/sessions/{session_id}/live-transcription/close',
      ...options,
    })
  }

  /**
   * Read the answer to one question out of one recording.
   * Takes a whole recording of the respondent speaking their answer to a single question and returns the answer that question takes, or a sentence to read back to them when what they said cannot be used. This is the non-streaming counterpart to the ``live-transcription`` endpoints: the answer arrives in one response instead of over an SSE stream, and the transcript is not the product — a transcript alone is the wrong thing to put in the field, because "rene at caplena dot com" is not an email address and "fifteen" is not a point on a scale of ten.
   *
   * Body is the recording itself, in any audio format the analysis models read (``audio/webm``, ``audio/mp4``, ``audio/ogg``, ``audio/wav``, ``audio/pcm``, …), of at most 10485760 bytes. The format is taken from the bytes, not from ``Content-Type``.
   *
   * ``X-Signal-Question`` must carry the id of the question the recording answers: it is what the recording is read against, and it decides both what a valid answer looks like and what the feedback talks about.
   *
   * Either ``value`` or ``feedback`` carries the outcome. A recording that answered the question comes back with a ``value`` and no ``feedback``; one that did not comes back with ``feedback`` — a single sentence in the survey's language, addressed to the respondent — and a null ``value``. A recording with nothing usable in it and nothing worth saying about it has neither, while one the models could not read at all is an error (``signals.voice_input.failed``) rather than an empty answer. ``transcript`` is what was said, for showing them what was heard; it is never the answer to store. Nothing is persisted: submit the answer through the session endpoint as usual.
   */
  public static collectVoiceInput<ThrowOnError extends boolean = true>(
    options: Options<CollectVoiceInputData, ThrowOnError>
  ) {
    return (options.client ?? _heyApiClient).post<CollectVoiceInputResponse, CollectVoiceInputError, ThrowOnError>({
      url: '/sessions/{session_id}/voice-input',
      ...options,
      headers: {
        'Content-Type': 'audio/aac',
        ...options?.headers,
      },
    })
  }
}
